/**
 * authSync.js — Synchronisation multi-onglet de l'authentification
 *
 * Mécanisme : localStorage "storage" event (natif, fiable, cross-tab).
 * Chaque logout écrit un event JSON dans localStorage qui déclenche
 * le "storage" event dans tous les AUTRES onglets.
 *
 * Usage :
 *   import { initAuthSync, broadcastLogout, forceLogout } from '../auth/authSync';
 *
 *   // Root App — useEffect :
 *   useEffect(() => initAuthSync({
 *     onLogout: (reason) => { setUser(null); navigate('/'); }
 *   }), []);
 *
 *   // Bouton déconnexion :
 *   broadcastLogout('user_action');
 *   forceLogout('user_action');
 *
 *   // L'intercepteur 401 dans http.js gère automatiquement les appels API.
 */

// ─── Constante clé localStorage ───────────────────────────────
const AUTH_EVENT_KEY = 'skyapp:auth:event';
const LAST_ACTIVITY_KEY = 'skyapp:lastActivity';

// Durée unique d'inactivité avant déconnexion forcée (VAL session timeout).
export const SESSION_IDLE_TIMEOUT_MS = 10 * 60 * 1000;

// ─── État interne ─────────────────────────────────────────────
let _logoutInProgress = false;
let _onLogoutCallback = null;

/**
 * Enregistre la dernière activité utilisateur de façon persistante
 * (survit à la fermeture du navigateur). Associée au userId pour éviter
 * qu'un changement de compte ne réutilise un timestamp obsolète.
 *
 * @param {string} userId
 */
export function recordUserActivity(userId) {
  if (!userId) return;
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, JSON.stringify({ userId, timestamp: Date.now() }));
  } catch {
    // Silently ignore (private browsing, quota exceeded)
  }
}

/**
 * Récupère le timestamp de dernière activité persistant pour ce userId.
 * Renvoie null si absent, corrompu, ou associé à un autre utilisateur.
 *
 * @param {string} userId
 * @returns {number|null}
 */
export function getLastActivity(userId) {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || data.userId !== userId || typeof data.timestamp !== 'number') return null;
    return data.timestamp;
  } catch {
    return null;
  }
}

/**
 * Supprime le timestamp de dernière activité persistant.
 */
export function clearLastActivity() {
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch {
    // Silently ignore
  }
}

/**
 * Détermine si la session doit être considérée expirée pour inactivité,
 * à partir du timestamp persistant (survit à la fermeture du navigateur).
 * Absence de trace fiable => pas de logout forcé (cf. section "cas lastActivity absent").
 *
 * @param {string} userId
 * @param {number} [timeoutMs=SESSION_IDLE_TIMEOUT_MS]
 * @returns {boolean}
 */
export function isSessionExpired(userId, timeoutMs = SESSION_IDLE_TIMEOUT_MS) {
  const last = getLastActivity(userId);
  if (last == null) return false;
  return (Date.now() - last) >= timeoutMs;
}

/**
 * Broadcast un événement de logout à tous les autres onglets.
 * Utilise localStorage pour déclencher le "storage" event cross-tab.
 * Math.random() garantit un changement de valeur même si la reason est identique.
 *
 * @param {string} reason
 */
export function broadcastLogout(reason) {
  try {
    localStorage.setItem(AUTH_EVENT_KEY, JSON.stringify({
      type: 'LOGOUT',
      reason,
      at: Date.now(),
      id: Math.random()
    }));
  } catch {
    // Silently ignore (private browsing, quota exceeded)
  }
}

/**
 * Nettoie tous les tokens d'authentification du localStorage.
 */
export function clearAuthStorage() {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('auth:lastLogout');
  clearLastActivity();

  // Supprimer les clés Supabase sb-*-auth-token (clé dynamique)
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
}

/**
 * Force la déconnexion immédiate de CET onglet.
 * - Nettoie le storage
 * - Appelle le callback onLogout (pour reset le state React + redirection)
 *
 * Safe à appeler plusieurs fois (flag _logoutInProgress).
 *
 * @param {string} reason - 'user_action' | '401' | 'refresh_failed' | 'multi_tab' | etc.
 */
export function forceLogout(reason = 'unknown') {
  if (_logoutInProgress) return;
  _logoutInProgress = true;

  if (process.env.NODE_ENV === 'development') {
    console.warn(`[authSync] forceLogout — reason: ${reason}`);
  }

  // 1) Nettoyer le storage
  clearAuthStorage();

  // 2) Appeler le callback React (setUser(null), navigate, etc.)
  if (_onLogoutCallback) {
    try {
      _onLogoutCallback(reason);
    } catch (e) {
      console.error('[authSync] onLogout callback error:', e);
    }
  }

  // 3) Reset le flag après un court délai (anti-boucle)
  setTimeout(() => { _logoutInProgress = false; }, 1000);
}

/**
 * Initialise l'écoute multi-onglet.
 * À appeler UNE SEULE FOIS dans le useEffect root de App().
 *
 * @param {{ onLogout: (reason: string) => void }} options
 * @returns {() => void} cleanup function pour useEffect
 */
export function initAuthSync({ onLogout }) {
  _onLogoutCallback = onLogout;

  const handleStorageEvent = (e) => {
    if (e.key !== AUTH_EVENT_KEY) return;
    try {
      const data = JSON.parse(e.newValue);
      if (data?.type === 'LOGOUT') {
        forceLogout('multi_tab');
      }
    } catch {
      // Ignore parse errors
    }
  };

  window.addEventListener('storage', handleStorageEvent);

  // Cleanup function pour useEffect
  return () => {
    window.removeEventListener('storage', handleStorageEvent);
    _onLogoutCallback = null;
  };
}
